import { Box, ButtonBase, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import lauraImg from "assets/home/laura.png";
import maddyImg from "assets/home/maddy_ugc.jpg";
import taylorImg from "assets/home/taylor.png";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useMemo, useState } from "react";
import { Plans } from "schemas/payments";

import VideoDialog from "components/Styled/Video/VideoDialog";
import {
  FREE_BENTO_VIDEO_LINK,
  FREE_IMAGE_PREVIEW_LINK,
  PRO_IMAGE_PREVIEW_LINK,
  PRO_IMAGE_PREVIEW_LINK_SQUARE,
  PRO_SUBSCRIPTION_VIDEO_LINK,
  VA_IMAGE_PREVIEW_LINK,
  VA_SUBSCRIPTION_VIDEO_LINK,
} from "constants/influencer";
import { isInfluencer, isUgcCreator } from "utils/influencer";
import { trackEvent } from "utils/tracking";

import { getVideosForUser } from "../TipsFromCreators";

type ResourceType = "tutorial" | "article" | "coach" | "tip";
type FilterKey = "all" | ResourceType;

type ResourceItem = {
  type: ResourceType;
  label: string;
  title: string;
  meta: string;
  url: string;
  thumbnail?: string;
  isVideo?: boolean;
  trackingEvent: string;
  trackingProps: Record<string, unknown>;
  inlineVideo?: {
    link: string;
    preview: string;
    widthPercentage?: number;
  };
};

const COACH_ITEMS: ResourceItem[] = [
  {
    type: "coach",
    label: "Coach",
    title: "1:1 VIP coaching with Laura",
    meta: "Rebuild your UGC strategy",
    url: "https://laurasocials.com/vipday",
    thumbnail: lauraImg,
    trackingEvent: "Partner Offers View Offer Clicked",
    trackingProps: { Offer: "Laura VIP Coaching" },
  },
  {
    type: "coach",
    label: "Coach",
    title: "Taylor's Creator Partnership Vault",
    meta: "1,300+ creator resources",
    url: "https://shop.taylorsocials.com/creator-partnership-vault/?ref=bento",
    thumbnail: taylorImg,
    trackingEvent: "Partner Offers View Offer Clicked",
    trackingProps: { Offer: "Taylor Creator Partnership Vault" },
  },
  {
    type: "coach",
    label: "Coach",
    title: "Get paid to travel with UGC (@maddyoutdoors)",
    meta: "Free training",
    url: "https://paidtotravel.buzzsprout.com/",
    thumbnail: maddyImg,
    trackingEvent: "Partner Offers View Offer Clicked",
    trackingProps: { Offer: "Maddy Travel UGC" },
  },
];

const TUTORIAL_ITEMS: ResourceItem[] = [
  {
    type: "tutorial",
    label: "Tutorial",
    title: "Getting Started with Bento",
    meta: "Learn how to pitch brands and land deals",
    url: "https://onbento.notion.site/",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "Getting Started" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Recommendations",
    meta: "Make the most of personalized recs",
    url: "https://onbento.notion.site/how-to-use-recommendations",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Recommendations" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Inbox",
    meta: "Manage brand conversations in one place",
    url: "https://onbento.notion.site/how-to-use-inbox",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Inbox" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Campaigns",
    meta: "Schedule emails in bulk effectively",
    url: "https://onbento.notion.site/how-to-use-campaigns",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Campaigns" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "Get Full Value of Pro Subscription",
    meta: "Higher limits, filters & contacts",
    url: "https://onbento.notion.site/get-full-value-pro",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Get Full Value of Pro Subscription" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "Get Full Value of Virtual Assistant",
    meta: "Combine automation with tailored pitches",
    url: "https://onbento.notion.site/get-full-value-va",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: {
      resource: "How to Get Full Value of Virtual Assistant Subscription",
    },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Use Your Bento Metrics",
    meta: "Improve outreach with summary metrics",
    url: "https://hatchways.notion.site/how-to-use-metrics",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Use Your Bento Metrics" },
  },
  {
    type: "tutorial",
    label: "Tutorial",
    title: "How to Get Bento Credits",
    meta: "Earn credits by referring friends",
    url: "https://onbento.notion.site/how-to-get-bento-credits",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Get Bento Credits" },
  },
];

const ARTICLE_ITEMS: ResourceItem[] = [
  {
    type: "article",
    label: "Article",
    title: "What is Pitching",
    meta: "A practical pitching guide for creators",
    url: "https://www.onbento.com/post/sales-partnerships-manual",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "What is Pitching" },
  },
  {
    type: "article",
    label: "Article",
    title: "How to Pitch",
    meta: "Approach brands that align with you",
    url: "https://www.onbento.com/post/sales-manual-start-selling",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Pitch" },
  },
  {
    type: "article",
    label: "Article",
    title: "How to Price & Negotiate",
    meta: "Price your work strategically",
    url: "https://www.onbento.com/post/sales-manual-pricing-and-contacts",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Price & Negotiate" },
  },
  {
    type: "article",
    label: "Article",
    title: "Build a Scalable Process",
    meta: "Long-term relationships with brands",
    url: "https://www.onbento.com/post/sales-manual-building-a-sustainable-process",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Build a Scalable Process" },
  },
  {
    type: "article",
    label: "Article",
    title: "Prevent Spam or Blocked Emails",
    meta: "Best practices for deliverability",
    url: "https://onbento.notion.site/prevent-getting-blocked",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: {
      resource: "How to Prevent Your Email from Going Into Spam",
    },
  },
  {
    type: "article",
    label: "Article",
    title: "Optimizing your pitching process",
    meta: "1K brands in 4 months, no audience",
    url: "https://www.onbento.com/post/what-i-learned-from-pitching-to-1k-brands",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "Optimizing your pitching process" },
  },
  {
    type: "article",
    label: "Article",
    title: "How to Close a Brand Deal",
    meta: "Turn interest into a signed deal",
    url: "https://onbento.notion.site/how-to-close-a-brand-deal?pvs=4",
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to Close a Brand Deal" },
  },
  {
    type: "article",
    label: "Article",
    title: "Write a Better Pitch Email",
    meta: "Watch · Personal pitches that convert",
    url: "https://www.loom.com/share/7e510141384149e7962f80e412882a56",
    isVideo: true,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "How to write a better Pitch Email" },
  },
  {
    type: "article",
    label: "Article",
    title: "Rewriting Creators Pitch Playlist",
    meta: "Watch · Real pitches, rewritten",
    url: "https://www.youtube.com/watch?v=KRU62IuHbaA&list=PLuAqaH-DOuVjL-wW5rEWPf3E-jYmPJ26N&index=8&ab_channel=Jaclyn",
    isVideo: true,
    trackingEvent: "Resources Hub Read More Clicked",
    trackingProps: { resource: "Rewriting Creators Pitch Playlist" },
  },
];

const TYPE_COLORS: Record<ResourceType, { dot: string; text: string }> = {
  tutorial: { dot: "#b9a311", text: "#8a7100" },
  article: { dot: "#8084FF", text: "#5A5EE8" },
  coach: { dot: "#014638", text: "#165C4E" },
  tip: { dot: "#E4405F", text: "#C13584" },
};

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "tutorial", label: "Tutorials" },
  { key: "tip", label: "Tips" },
  { key: "coach", label: "Coaches" },
  { key: "article", label: "Articles" },
];

const ResourcesSection = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { subscription } = useContext(SubscriptionContext);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expanded, setExpanded] = useState(false);
  const [activeVideo, setActiveVideo] = useState<{
    link: string;
    preview: string;
    widthPercentage?: number;
    title: string;
  } | null>(null);

  const isVaUser = !!currentOrg?.isVaUser;
  const isPro =
    !!subscription?.subscription?.planName &&
    !!subscription?.isSubscribed &&
    [Plans.PRO, Plans.PRO_YEARLY].includes(subscription.subscription.planName);

  const planVideo = useMemo<ResourceItem>(() => {
    if (isVaUser) {
      return {
        type: "tutorial",
        label: "Tutorial",
        title: "Make the Most of Your Virtual Assistant Subscription",
        meta: "Watch · Plan walkthrough",
        url: "",
        thumbnail: VA_IMAGE_PREVIEW_LINK,
        isVideo: true,
        trackingEvent: "Onboarding Video Clicked",
        trackingProps: {
          title: "Make the Most of Your Virtual Assistant Subscription",
        },
        inlineVideo: {
          link: VA_SUBSCRIPTION_VIDEO_LINK,
          preview: VA_IMAGE_PREVIEW_LINK,
        },
      };
    }
    if (isPro) {
      return {
        type: "tutorial",
        label: "Tutorial",
        title: "Make the Most of Your Pro Subscription",
        meta: "Watch · Plan walkthrough",
        url: "",
        thumbnail: PRO_IMAGE_PREVIEW_LINK_SQUARE,
        isVideo: true,
        trackingEvent: "Onboarding Video Clicked",
        trackingProps: { title: "Make the Most of Your Pro Subscription" },
        inlineVideo: {
          link: PRO_SUBSCRIPTION_VIDEO_LINK,
          preview: PRO_IMAGE_PREVIEW_LINK,
          widthPercentage: 30,
        },
      };
    }
    return {
      type: "tutorial",
      label: "Tutorial",
      title: "How to Use Bento",
      meta: "Watch · Platform walkthrough",
      url: "",
      thumbnail: FREE_IMAGE_PREVIEW_LINK,
      isVideo: true,
      trackingEvent: "Onboarding Video Clicked",
      trackingProps: { title: "How to Use Bento" },
      inlineVideo: {
        link: FREE_BENTO_VIDEO_LINK,
        preview: FREE_IMAGE_PREVIEW_LINK,
      },
    };
  }, [isVaUser, isPro]);

  const tutorialItems = useMemo<ResourceItem[]>(
    () =>
      TUTORIAL_ITEMS.filter((item) => {
        if (item.title === "Get Full Value of Pro Subscription") return isPro;
        if (item.title === "Get Full Value of Virtual Assistant")
          return isVaUser;
        return true;
      }),
    [isPro, isVaUser],
  );

  const tipItems = useMemo<ResourceItem[]>(() => {
    const userIsUgc = isUgcCreator(profile);
    const userIsInfluencer = isInfluencer(profile);
    const brandDealJourney = profile?.brandDealJourney;
    return getVideosForUser(userIsUgc, userIsInfluencer, brandDealJourney).map(
      (video) => ({
        type: "tip",
        label: "Tip",
        title: video.title,
        meta: "Watch · Instagram tip",
        url: video.url,
        thumbnail: video.thumbnail,
        isVideo: true,
        trackingEvent: "Tips From Creators Video Clicked",
        trackingProps: {
          title: video.title,
          url: video.url,
          isUgcCreator: userIsUgc,
          isInfluencer: userIsInfluencer,
          brandDealJourney,
        },
      }),
    );
  }, [profile]);

  const allItems = useMemo<ResourceItem[]>(
    () => [
      planVideo,
      ...tipItems,
      ...COACH_ITEMS,
      ...ARTICLE_ITEMS,
      ...tutorialItems,
    ],
    [planVideo, tutorialItems, tipItems],
  );

  const counts = useMemo(
    () => ({
      all: allItems.length,
      tutorial: allItems.filter((i) => i.type === "tutorial").length,
      article: allItems.filter((i) => i.type === "article").length,
      coach: allItems.filter((i) => i.type === "coach").length,
      tip: allItems.filter((i) => i.type === "tip").length,
    }),
    [allItems],
  );

  const filtered = useMemo(
    () =>
      filter === "all"
        ? allItems
        : allItems.filter((item) => item.type === filter),
    [filter, allItems],
  );
  const VISIBLE_LIMIT = 4;
  const shown = expanded ? filtered : filtered.slice(0, VISIBLE_LIMIT);
  const hasMore = filtered.length > VISIBLE_LIMIT;

  const handleClick = (item: ResourceItem) => {
    trackEvent(item.trackingEvent, item.trackingProps);
    if (item.inlineVideo) {
      setActiveVideo({ ...item.inlineVideo, title: item.title });
      return;
    }
    window.open(item.url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F7F6F0",
        borderRadius: 4,
        p: { xs: 2, md: 3 },
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: "InterTight, Inter, sans-serif",
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: "-0.005em",
            }}
          >
            Resources
          </Typography>
          <Box sx={{ display: "flex", gap: 0.25, flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <ButtonBase
                key={f.key}
                onClick={() => setFilter(f.key)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontFamily: "InterTight, Inter, sans-serif",
                  fontSize: 12,
                  fontWeight: 500,
                  color: filter === f.key ? "#fff" : "rgba(0,0,0,0.6)",
                  backgroundColor: filter === f.key ? "#04060D" : "transparent",
                  borderRadius: 999,
                  px: 1.25,
                  py: 0.5,
                  "&:hover": {
                    color: filter === f.key ? "#fff" : "#04060D",
                  },
                }}
              >
                {f.label}
                <Box
                  component="span"
                  sx={{
                    opacity: 0.55,
                    fontWeight: 400,
                    fontSize: 11,
                  }}
                >
                  {counts[f.key]}
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
        {hasMore && (
          <ButtonBase
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.625,
              fontFamily: "InterTight, Inter, sans-serif",
              fontSize: 12,
              fontWeight: 500,
              color: "#776B5D",
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              "&:hover": { color: "#014638", textDecoration: "underline" },
            }}
          >
            {expanded ? "View less" : "View all"}
            <Box
              component="i"
              className={
                expanded ? "fa-solid fa-arrow-up" : "fa-solid fa-arrow-down"
              }
              sx={{ fontSize: 10 }}
            />
          </ButtonBase>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 1,
        }}
      >
        {shown.map((item, idx) => (
          <ResourceTile
            key={`${item.type}-${idx}`}
            item={item}
            onClick={() => handleClick(item)}
            compact={isSmall}
          />
        ))}
      </Box>

      {activeVideo && (
        <VideoDialog
          open
          link={activeVideo.link}
          videoPreview={activeVideo.preview}
          widthPercentage={activeVideo.widthPercentage}
          title={activeVideo.title}
          handleClose={() => setActiveVideo(null)}
        />
      )}
    </Box>
  );
};

interface TileProps {
  item: ResourceItem;
  onClick: () => void;
  compact?: boolean;
}

const ResourceTile = ({ item, onClick }: TileProps) => {
  const colors = TYPE_COLORS[item.type];
  const hasThumb = !!item.thumbnail;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex",
        gap: 1.25,
        alignItems: "center",
        backgroundColor: "#fff",
        borderRadius: 2.5,
        p: 1.25,
        textAlign: "left",
        justifyContent: "flex-start",
        transition: "transform 0.12s, box-shadow 0.12s",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        },
      }}
    >
      {hasThumb && (
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: 1.5,
            backgroundImage: `url(${item.thumbnail})`,
            backgroundSize: "cover",
            backgroundPosition: "center top",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {item.isVideo && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.25)",
              }}
            >
              <Box
                component="i"
                className="fa-solid fa-play"
                sx={{ color: "#fff", fontSize: 16 }}
              />
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.625,
            mb: 0.25,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: colors.dot,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontFamily: "InterTight, Inter, sans-serif",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: colors.text,
            }}
          >
            {item.label}
            {item.isVideo && " · Video"}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: "InterTight, Inter, sans-serif",
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: 1.3,
            color: "#04060D",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.title}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: "rgba(0,0,0,0.55)",
            mt: 0.25,
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.meta}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

export default ResourcesSection;
